import {FC, useEffect, useMemo, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {DashboardTrends} from '../core/_models'
import {getCSSVariableValue} from '../../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../../_metronic/partials'

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

/** Converte horas decimais para string no padrão "hh:mm". */
function formatHoursToHhMm(decimalHours: number): string {
  const h = Math.floor(decimalHours)
  const m = Math.round((decimalHours % 1) * 60)
  return `${h}:${m.toString().padStart(2, '0')}`
}

function formatMonth(ym: string): string {
  const [y, m] = ym.split('-')
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`
}

const ChartCard: FC<{title: string; subtitle?: string; children: React.ReactNode}> = ({title, subtitle, children}) => (
  <div className='card card-flush h-100'>
    <div className='card-header pt-5'>
      <h3 className='card-title align-items-start flex-column'>
        <span className='card-label fw-bold'>{title}</span>
        {subtitle && <span className='text-muted fw-semibold fs-7 mt-1'>{subtitle}</span>}
      </h3>
    </div>
    <div className='card-body pt-2'>{children}</div>
  </div>
)

export interface DashboardTrendsChartsProps {
  trends: DashboardTrends | undefined
  isLoading: boolean
  error: unknown
  refetch: () => void
}

export const DashboardTrendsCharts: FC<DashboardTrendsChartsProps> = ({trends, isLoading, error, refetch}) => {
  const {mode} = useThemeMode()
  const marginRef = useRef<HTMLDivElement>(null)
  const closedRef = useRef<HTMLDivElement>(null)
  const hoursRef = useRef<HTMLDivElement>(null)
  const statusRef = useRef<HTMLDivElement>(null)

  const labelColor = useMemo(() => getCSSVariableValue('--kt-gray-500'), [])
  const borderColor = useMemo(() => getCSSVariableValue('--kt-gray-200'), [])
  const primary = useMemo(() => getCSSVariableValue('--kt-primary'), [])
  const success = useMemo(() => getCSSVariableValue('--kt-success'), [])
  const warning = useMemo(() => getCSSVariableValue('--kt-warning'), [])

  useEffect(() => {
    if (!trends || !marginRef.current) return

    const opts: ApexOptions = {
      series: [{name: 'Margem %', data: trends.profit_margin_evolution.map((d) => d.value)}],
      chart: {type: 'line', height: 260, toolbar: {show: false}, fontFamily: 'inherit'},
      stroke: {curve: 'smooth', width: 2},
      xaxis: {categories: trends.profit_margin_evolution.map((d) => formatMonth(d.month)), labels: {style: {colors: labelColor, fontSize: '11px'}}, axisBorder: {show: false}, axisTicks: {show: false}},
      yaxis: {labels: {style: {colors: labelColor}, formatter: (v) => v + '%'}, min: 0, max: 100},
      colors: [primary],
      grid: {borderColor, strokeDashArray: 4, yaxis: {lines: {show: true}}},
      dataLabels: {enabled: false},
      legend: {show: false},
      tooltip: {y: {formatter: (v) => (v != null ? v + '%' : '')}},
    }
    const ch = new ApexCharts(marginRef.current, opts)
    ch.render()
    return () => { ch.destroy() }
  }, [trends, mode, labelColor, borderColor, primary])

  useEffect(() => {
    if (!trends || !closedRef.current) return

    const opts: ApexOptions = {
      series: [{name: 'Concluídos', data: trends.closed_projects_evolution.map((d) => d.value)}],
      chart: {type: 'bar', height: 260, toolbar: {show: false}, fontFamily: 'inherit'},
      plotOptions: {bar: {borderRadius: 4, columnWidth: '55%'}},
      xaxis: {categories: trends.closed_projects_evolution.map((d) => formatMonth(d.month)), labels: {style: {colors: labelColor, fontSize: '11px'}}, axisBorder: {show: false}, axisTicks: {show: false}},
      yaxis: {labels: {style: {colors: labelColor}}},
      colors: [success],
      grid: {borderColor, strokeDashArray: 4, yaxis: {lines: {show: true}}},
      dataLabels: {enabled: false},
      legend: {show: false},
    }
    const ch = new ApexCharts(closedRef.current, opts)
    ch.render()
    return () => { ch.destroy() }
  }, [trends, mode, labelColor, borderColor, success])

  useEffect(() => {
    if (!trends || !hoursRef.current) return

    const opts: ApexOptions = {
      series: [{name: 'Horas', data: trends.hours_tracked_evolution.map((d) => d.value)}],
      chart: {type: 'bar', height: 260, toolbar: {show: false}, fontFamily: 'inherit'},
      plotOptions: {bar: {borderRadius: 4, columnWidth: '55%'}},
      xaxis: {categories: trends.hours_tracked_evolution.map((d) => formatMonth(d.month)), labels: {style: {colors: labelColor, fontSize: '11px'}}, axisBorder: {show: false}, axisTicks: {show: false}},
      yaxis: {labels: {style: {colors: labelColor}}},
      colors: [primary],
      grid: {borderColor, strokeDashArray: 4, yaxis: {lines: {show: true}}},
      dataLabels: {enabled: false},
      legend: {show: false},
      tooltip: {y: {formatter: (v) => (v != null ? v + ' h' : '')}},
    }
    const ch = new ApexCharts(hoursRef.current, opts)
    ch.render()
    return () => { ch.destroy() }
  }, [trends, mode, labelColor, borderColor, primary])

  useEffect(() => {
    if (!trends || !statusRef.current) return

    const s = trends.projects_by_status
    const total = s.delayed + s.in_progress + s.closed
    if (total === 0) return

    const opts: ApexOptions = {
      series: [s.delayed, s.in_progress, s.closed],
      chart: {type: 'donut', height: 260, fontFamily: 'inherit'},
      labels: ['Atrasados', 'Em andamento', 'Concluídos'],
      colors: [warning, primary, success],
      legend: {position: 'bottom', fontSize: '12px', labels: {colors: labelColor}},
      dataLabels: {enabled: true},
      plotOptions: {pie: {donut: {size: '65%'}}},
    }
    const ch = new ApexCharts(statusRef.current, opts)
    ch.render()
    return () => { ch.destroy() }
  }, [trends, mode, labelColor, warning, primary, success])

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center py-10'>
        <span className='spinner-border spinner-border-lg text-primary' role='status' aria-hidden='true' />
        <span className='ms-3 fs-6 text-muted'>Carregando análise...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className='alert alert-warning d-flex align-items-center'>
        <i className='bi bi-exclamation-triangle fs-2 me-3' />
        <div className='flex-grow-1'>
          Não foi possível carregar os dados. Verifique sua conexão.
          <button type='button' className='btn btn-sm btn-warning ms-3' onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </div>
    )
  }

  if (!trends) {
    return <div className='text-muted fs-7 py-4'>Dados não disponíveis.</div>
  }

  return (
    <div>
      <div className='row g-5 mb-6'>
        <div className='col-12 col-lg-6'>
          <ChartCard title='Evolução da margem de lucro' subtitle='Últimos 6 meses — média dos projetos encerrados no mês'>
            <div ref={marginRef} style={{height: '260px'}} />
          </ChartCard>
        </div>
        <div className='col-12 col-lg-6'>
          <ChartCard title='Projetos concluídos' subtitle='Últimos 6 meses'>
            <div ref={closedRef} style={{height: '260px'}} />
          </ChartCard>
        </div>
      </div>

      <div className='row g-5 mb-6'>
        <div className='col-12 col-lg-6'>
          <ChartCard title='Horas lançadas' subtitle='Últimos 3 meses'>
            <div ref={hoursRef} style={{height: '260px'}} />
          </ChartCard>
        </div>
        <div className='col-12 col-lg-6'>
          <ChartCard title='Projetos por status' subtitle='Situação atual'>
            <div ref={statusRef} style={{height: '260px'}} />
          </ChartCard>
        </div>
      </div>

      <div className='row'>
        <div className='col-12'>
          <ChartCard title='Top 5 clientes por horas lançadas' subtitle='Mês atual'>
            {trends.top5_clients_by_hours.length === 0 ? (
              <div className='text-muted fs-7'>Nenhum lançamento de horas no período.</div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bold text-gray-600'>
                      <th className='min-w-120px'>Cliente</th>
                      <th className='min-w-80px text-end'>Horas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trends.top5_clients_by_hours.map((c) => (
                      <tr key={c.client_id}>
                        <td className='fw-semibold'>{c.client_name}</td>
                        <td className='text-end'>{formatHoursToHhMm(c.hours)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </div>
      </div>
    </div>
  )
}
